import React, { useState, useEffect } from 'react';
import { FaFolder, FaTimes, FaPlus } from 'react-icons/fa';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EditContentModal = ({ contentEditModal, setContentEditModal, moduleId, fetchCourseContent }) => {
  const [moduleName, setModuleName] = useState(''); // ✅ Initialize as string

  // Fetch module details when moduleId changes
  useEffect(() => {
    const fetchCourseModule = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const response = await fetch(`${BASE_URL}/courses/module/${moduleId}/`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "x-api-key": process.env.REACT_APP_X_API_KEY
          },
        });

        if (!response.ok) throw new Error("Failed to fetch module");

        const data = await response.json();
        setModuleName(data?.title || ''); // Ensure it's always a string
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };

    if (moduleId) fetchCourseModule();
  }, [moduleId]);

  // Submit on Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') handleEdit();
  };

  // Close modal if overlay is clicked
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) setContentEditModal(false);
  };

  // Edit module
  const handleEdit = async () => {
    if (moduleName.trim() === '') return;

    try {
      const res = await fetch(`${BASE_URL}/courses/module/${moduleId}/update/`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_X_API_KEY
        },
        body: JSON.stringify({ title: moduleName }),
      });

      const data = await res.json();
      if (res.ok) {
        alert('Course Module Updated Successfully');
        fetchCourseContent();
        setModuleName(''); // Clear input after success
        setContentEditModal(false);
      } else {
        console.error("Update failed:", data);
        alert('Failed to update module. Please try again.');
      }
    } catch (error) {
      console.error("Error updating module:", error.message);
    }
  };

  if (!contentEditModal) return null;

  return (
   <div
  className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 transition-all duration-300 ${
    contentEditModal ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
  }`}
  onClick={handleOverlayClick}
>
  <div
    className={`bg-white rounded-2xl w-full max-w-lg shadow-xl transform transition-all duration-300 ${
      contentEditModal ? "translate-y-0 scale-100" : "-translate-y-7 scale-95"
    }`}
    onClick={(e) => e.stopPropagation()}
  >
    {/* Modal Header */}
    <div className="p-6 border-b border-gray-100">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-[#e8f0f7] rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6 text-[#0f6abf]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Module</h2>
            <p className="text-sm text-gray-500 mt-0.5">Edit module for your course</p>
          </div>
        </div>
        <button
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors"
          onClick={() => setContentEditModal(false)}
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    {/* Modal Content */}
    <div className="p-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-4 bg-[#0f6abf] rounded-full"></div>
          <label className="text-sm font-semibold text-gray-700">
            Module Name
          </label>
        </div>
        <input
          type="text"
          placeholder="Enter module name..."
          value={moduleName}
          onChange={(e) => setModuleName(e.target.value)}
          onKeyPress={handleKeyPress}
          autoFocus
          className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#0f6abf] focus:ring-2 focus:ring-[#0f6abf]/20 transition-all"
        />
      </div>
    </div>

    {/* Modal Actions */}
    <div className="px-6 py-4 bg-gray-50/50 border-t border-gray-100">
      <div className="flex items-center justify-end gap-3">
        <button
          className="px-5 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
          onClick={() => setContentEditModal(false)}
        >
          Cancel
        </button>
        <button
          className={`px-5 py-2.5 rounded-xl font-medium transition-colors flex items-center gap-2 ${
            moduleName.trim()
              ? "bg-[#0f6abf] text-white hover:bg-[#0a5ca8]" 
              : "bg-gray-200 text-gray-400 cursor-not-allowed"
          }`}
          onClick={handleEdit}
          disabled={!moduleName.trim()}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Edit Module
        </button>
      </div>
    </div>
  </div>
</div>
  );
};

export default EditContentModal;
